import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 100;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const STATUS_LABELS = {
  untouched: "Untouched", hunted: "Hunted", parked: "Parked", not_fit: "Not a fit", unverified: "Unverified", pending: "Pending check",
};

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
};

export const useMarketMap = (userId) => {
  const [firms, setFirms] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState({ status: "untouched", q: "", sort: "fit" });
  const [job, setJob] = useState(null);
  const [hunting, setHunting] = useState(null);
  const pollRef = useRef(false);
  const q = useDebounced(filters.q, 300);

  const fetchPage = useCallback(async (offset) => {
    const { data } = await axios.get(`${API}/market/${userId}`, { params: { status: filters.status, sort: filters.sort, q, limit: PAGE, offset } });
    return data;
  }, [userId, filters.status, filters.sort, q]);

  const load = useCallback(async () => {
    try {
      const data = await fetchPage(0);
      setFirms(data.firms || []);
      setTotal(data.total || 0);
      setStats(data.stats || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the market map");
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchPage(firms.length);
      setFirms((prev) => [...prev, ...(data.firms || [])]);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load more firms");
    } finally {
      setLoadingMore(false);
    }
  };

  const pollJob = useCallback(async () => {
    if (pollRef.current) return;
    pollRef.current = true;
    try {
      for (let i = 0; i < 150; i++) {
        const { data } = await axios.get(`${API}/market/${userId}/job`);
        setJob(data);
        if (data.status !== "running") {
          if (data.status === "done") toast.success(`Market map grew by ${data.result?.added ?? 0} firms${data.result?.pendingLeft ? ` · ${data.result.pendingLeft} queued for verification` : ""}`);
          if (data.status === "failed") toast.error(data.error || "Expansion failed");
          await load();
          return;
        }
        await sleep(2000);
      }
    } finally {
      pollRef.current = false;
    }
  }, [userId, load]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/market/${userId}/job`).then(({ data }) => { setJob(data); if (data.status === "running") pollJob(); }).catch(() => {});
  }, [userId, pollJob]);

  const expand = async () => {
    try {
      await axios.post(`${API}/market/${userId}/expand`);
      setJob({ status: "running", stage: "Starting", progress: 1 });
      toast.message("Mapping your market — this takes a minute or two");
      pollJob();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the expansion");
    }
  };

  const setStatus = async (firm, status) => {
    try {
      const { data } = await axios.patch(`${API}/market/${userId}/firms/${firm.firmId}`, { status });
      const next = data.status;
      setFirms((prev) => prev.map((f) => (f.firmId === firm.firmId ? { ...f, status: next } : f)).filter((f) => filters.status === "all" || f.status === filters.status));
      setTotal((t) => (filters.status === "all" || next === filters.status ? t : Math.max(0, t - 1)));
      setStats((s) => s && { ...s, [firm.status]: Math.max(0, (s[firm.status] || 0) - 1), [next]: (s[next] || 0) + 1 });
      toast.success(`${firm.companyName} → ${STATUS_LABELS[next]}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the firm");
    }
  };

  const huntHere = async (firm) => {
    setHunting(firm.firmId);
    try {
      const { data } = await axios.post(`${API}/agent/hunt/${userId}`, null, { params: { prompt: `Find the right buyer at ${firm.companyName}` } });
      let result = data;
      if (data.async && data.jobId) {
        for (let i = 0; i < 90; i++) {
          await sleep(2000);
          const { data: j } = await axios.get(`${API}/agent/hunt/job/${data.jobId}`);
          if (j.status === "done") { result = j.result; break; }
          if (j.status === "failed") throw new Error(j.error || "Hunt failed");
        }
      }
      const n = (result.companiesFound || []).length;
      toast.success(n ? `${n} buyer${n > 1 ? "s" : ""} found at ${firm.companyName} — review them in Command` : `No verified buyer found at ${firm.companyName} yet`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Hunt failed");
    } finally {
      setHunting(null);
    }
  };

  return { firms, total, stats, loading, loadingMore, loadMore, hasMore: firms.length < total, filters, setFilters, job, expand, setStatus, huntHere, hunting, reload: load };
};
